/**
 * A tiny, dependency-free external store + React-16-compatible selector hook.
 *
 * Avenue C (state-management layer) evaluates replacing MAA's context+hooks
 * tangle with a store so state updates notify only the components that select
 * the changed slice — instead of re-rendering every consumer of a Provider each
 * time any field changes. No dependency is added: Zustand/Redux aren't
 * installable in this environment, and this ~40-line primitive is enough to
 * demonstrate and measure the pattern. React 16.13 has no `useSyncExternalStore`,
 * so `useStore` implements the subscribe-with-selector pattern by hand.
 */
import { useEffect, useRef, useState } from 'react';
import { produce } from 'immer';

// DEV-only action log. When a write passes an action `name`, dev builds print a
// `{ action, patch, prev, next }` line so every state transition is a greppable,
// ordered timeline (the "trace any process" goal). Gated OFF in production and
// under test (so suites stay quiet); computed once at module load.
// eslint-disable-next-line
const DEV_LOG = typeof import.meta !== 'undefined' && !!import.meta.env
  && import.meta.env.DEV === true && !import.meta.env.VITEST && import.meta.env.MODE !== 'test';

/**
 * @param {object} initialState
 * @returns {{ getState: () => object, setState: (patch) => void, subscribe: (fn) => (() => void) }}
 */
export const createStore = (initialState) => {
  let state = initialState;
  const listeners = new Set();

  const getState = () => state;

  // Accepts a partial object or an updater fn; shallow-merges and notifies. An
  // optional `name` labels the write for the DEV action log (WS-1.3) — no effect
  // on behavior, and never logs in prod/test.
  const setState = (patch, name) => {
    const partial = typeof patch === 'function' ? patch(state) : patch;
    if (partial === state) return;
    const prev = state;
    state = { ...state, ...partial };
    if (DEV_LOG && name) {
      // eslint-disable-next-line no-console
      console.debug('[maa-store]', name, { patch: partial, prev, next: state });
    }
    listeners.forEach((fn) => fn(state));
  };

  const subscribe = (fn) => {
    listeners.add(fn);
    return () => listeners.delete(fn);
  };

  // Selector-scoped subscription: `listener(selected, prev)` fires only when the
  // selected slice changes per `equalityFn` (default Object.is). Lets non-React
  // code react to one field without a hand-rolled ref-bridge — e.g. a hook that
  // must read a store value fresh inside a callback. Mirrors zustand's
  // subscribeWithSelector; returns an unsubscribe.
  const subscribeWithSelector = (selector, listener, options = {}) => {
    const { equalityFn = Object.is, fireImmediately = false } = options;
    let current = selector(state);
    if (fireImmediately) listener(current, current);
    return subscribe((nextState) => {
      const next = selector(nextState);
      if (!equalityFn(current, next)) {
        const prev = current;
        current = next;
        listener(next, prev);
      }
    });
  };

  return {
    getState, setState, subscribe, subscribeWithSelector,
  };
};

/**
 * Subscribe to a slice of a store. The component re-renders only when the
 * selected value changes (per `isEqual`, default Object.is) — reading a stable
 * value (an action fn, an unchanged field) never triggers a re-render.
 *
 * @param {object} store - a createStore() instance
 * @param {(state: object) => any} selector
 * @param {(a: any, b: any) => boolean} [isEqual]
 */
export const useStore = (store, selector, isEqual = Object.is) => {
  const [selected, setSelected] = useState(() => selector(store.getState()));
  // Keep the latest selected value in a ref so the subscription closure always
  // compares against what was last rendered, without re-subscribing per render.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  useEffect(() => {
    const check = (state) => {
      const next = selector(state);
      if (!isEqual(selectedRef.current, next)) {
        selectedRef.current = next;
        setSelected(next);
      }
    };
    const unsubscribe = store.subscribe(check);
    // State may have changed between the initial render and this subscription.
    check(store.getState());
    return unsubscribe;
  }, [store]);

  return selected;
};

/**
 * Merge several slice factories into one Zustand-style factory. Each slice is
 * `(set, get) => ({ ...sliceState, ...sliceActions })`; the returned objects are
 * shallow-merged into one flat state, so selectors read `state.field` directly.
 *
 * @param {...((set, get) => object)} sliceFactories
 * @returns {(set, get) => object}
 */
export const combineSlices = (...sliceFactories) => (set, get) => (
  sliceFactories.reduce((acc, factory) => ({ ...acc, ...factory(set, get) }), {})
);

/**
 * Build the central store from a slice factory. State stays flat (actions live in
 * state alongside their data), and slices receive `(set, get)` — so a slice can be
 * seeded here and read via selectors with no per-implementation coupling. Returns
 * the StoreApi ({ getState, setState, subscribe, subscribeWithSelector }) that
 * `useStore` selects from.
 *
 * @param {(set, get) => object} createSlices
 * @returns {{ getState, setState, subscribe, subscribeWithSelector }} the StoreApi
 */
export const createSlicedStore = (createSlices) => {
  const store = createStore({});
  store.setState(createSlices(store.setState, store.getState));
  return store;
};

/**
 * Shallow equality for use as `useStore`'s `isEqual`, so a component can select
 * several fields at once (`(s) => ({ a: s.a, b: s.b })`) and re-render only when one
 * of them actually changes — instead of every store update (a fresh object literal
 * always fails Object.is). Mirrors Zustand's `shallow`.
 *
 * @param {any} a
 * @param {any} b
 * @returns {boolean}
 */
export const shallow = (objA, objB) => {
  if (Object.is(objA, objB)) return true;
  if (typeof objA !== 'object' || objA === null || typeof objB !== 'object' || objB === null) {
    return false;
  }
  const keysA = Object.keys(objA);
  const keysB = Object.keys(objB);
  if (keysA.length !== keysB.length) return false;
  return keysA.every(
    (key) => Object.prototype.hasOwnProperty.call(objB, key) && Object.is(objA[key], objB[key])
  );
};

/**
 * Immer-powered setter for slices holding nested state. `createImmerSet(set, get)`
 * returns `(recipe, name?)` where `recipe(draft)` MUTATES a draft of the current
 * state; the immutable next state is committed via `set` (so `useStore` re-render
 * semantics are unchanged) and labeled `name` for the DEV action log. Immer's
 * structural sharing means untouched branches keep their reference identity — so
 * selectors on unchanged slices still bail out. Opt-in: flat slices keep using
 * `set({ field })` directly; reach for this only for nested aggregate updates.
 *
 * @param {(patch, name?) => void} set - a store's setState
 * @param {() => object} get - a store's getState
 * @returns {(recipe: (draft: object) => void, name?: string) => void}
 */
export const createImmerSet = (set, get) => (recipe, name) => set(produce(get(), recipe), name);
